import { useMemo } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import type { TelemetryEvent } from '../../api/telemetry';

interface VerificationData {
  overallPassed: boolean;
  lint: 'pass' | 'fail' | 'skip';
  typecheck: 'pass' | 'fail' | 'skip';
  unitTests: 'pass' | 'fail' | 'skip';
  unitTestsPassed?: number;
  unitTestsFailed?: number;
  unitTestsSkipped?: number;
}

interface Props {
  telemetryEvents: TelemetryEvent[];
}

function MicroBar({ passed, failed, skipped, total }: { passed: number; failed: number; skipped: number; total: number }) {
  if (total === 0) return <div className="h-1.5 w-full rounded-full bg-neutral-100" />;
  const pP = (passed / total) * 100;
  const pF = (failed / total) * 100;
  return (
    <div className="h-1.5 w-full rounded-full bg-neutral-100 overflow-hidden flex">
      {pP > 0 && <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pP}%` }} />}
      {pF > 0 && <div className="h-full bg-red-500 transition-all duration-500" style={{ width: `${pF}%` }} />}
    </div>
  );
}

function CheckRow({ label, passed, failed, skipped }: { label: string; passed: number; failed: number; skipped: number }) {
  const total = passed + failed + skipped;
  const rate = total - skipped > 0 ? Math.round((passed / (total - skipped)) * 100) : 0;
  const rateColor = rate === 100 ? 'text-emerald-600' : rate >= 70 ? 'text-amber-600' : rate > 0 ? 'text-red-600' : 'text-neutral-400';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-neutral-500">{label}</span>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-mono font-semibold tabular-nums ${rateColor}`}>
            {total - skipped > 0 ? `${rate}%` : '—'}
          </span>
          <span className="text-[9px] text-neutral-400 tabular-nums">
            {passed}/{total - skipped}
          </span>
        </div>
      </div>
      <MicroBar passed={passed} failed={failed} skipped={skipped} total={total} />
    </div>
  );
}

export function VerificationSummaryCard({ telemetryEvents }: Props) {
  const stats = useMemo(() => {
    // Pull verification data from story_execution_completed events
    const storyEvents = telemetryEvents.filter(
      e => e.eventType === 'story_execution_completed' || e.eventType === 'sprite_verification_completed'
    );

    let verified = 0;
    let lintPass = 0, lintFail = 0, lintSkip = 0;
    let tscPass = 0, tscFail = 0, tscSkip = 0;
    let testPass = 0, testFail = 0, testSkip = 0;
    let unitTestsPassed = 0, unitTestsFailed = 0;

    for (const e of storyEvents) {
      const v = (e.payload as Record<string, unknown>)?.verification as VerificationData | undefined;
      if (!v) continue;

      verified++;

      if (v.lint === 'pass') lintPass++;
      else if (v.lint === 'fail') lintFail++;
      else lintSkip++;

      if (v.typecheck === 'pass') tscPass++;
      else if (v.typecheck === 'fail') tscFail++;
      else tscSkip++;

      if (v.unitTests === 'pass') testPass++;
      else if (v.unitTests === 'fail') testFail++;
      else testSkip++;

      unitTestsPassed += v.unitTestsPassed ?? 0;
      unitTestsFailed += v.unitTestsFailed ?? 0;
    }

    return {
      verified,
      lint: { passed: lintPass, failed: lintFail, skipped: lintSkip },
      typecheck: { passed: tscPass, failed: tscFail, skipped: tscSkip },
      unitTests: { passed: testPass, failed: testFail, skipped: testSkip },
      totalUnitTests: { passed: unitTestsPassed, failed: unitTestsFailed },
    };
  }, [telemetryEvents]);

  if (stats.verified === 0) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-6 text-center">
        <ShieldCheck size={20} className="mx-auto text-neutral-300 mb-2" />
        <p className="text-[11px] text-neutral-400">No verification data yet</p>
      </div>
    );
  }

  const allPassing = stats.lint.failed === 0 && stats.typecheck.failed === 0 && stats.unitTests.failed === 0;

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-100 flex items-center justify-between">
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Verification</p>
        <div className="flex items-center gap-1.5">
          {allPassing ? (
            <ShieldCheck size={12} className="text-emerald-500" />
          ) : (
            <AlertTriangle size={12} className="text-amber-500" />
          )}
          <span className={`text-[10px] font-semibold ${allPassing ? 'text-emerald-600' : 'text-amber-600'}`}>
            {stats.verified} verified
          </span>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <CheckRow label="Lint" {...stats.lint} />
        <CheckRow label="TypeScript" {...stats.typecheck} />
        <CheckRow label="Unit Tests" {...stats.unitTests} />

        {/* Unit test aggregate */}
        {(stats.totalUnitTests.passed > 0 || stats.totalUnitTests.failed > 0) && (
          <div className="pt-2 border-t border-neutral-100 flex items-baseline justify-between">
            <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Test Cases</span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-emerald-600 tabular-nums">
                {stats.totalUnitTests.passed} passed
              </span>
              {stats.totalUnitTests.failed > 0 && (
                <span className="text-[10px] font-mono text-red-600 tabular-nums">
                  {stats.totalUnitTests.failed} failed
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
